import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Button } from 'reactstrap';

import {
  getGoogleFontCssUrl,
  getGoogleFontFileUrl,
} from '../Common/pageFontUtils';
import { addFlashMessage } from '../../../../helpers/FlashMessages';

const FontRow = ({
  font,
  previewText,
  allowDownloads,
  isEditing,
}) => {
  const intl = useIntl();
  const source = font.source || 'uploaded';
  const uploadedFontFamily = `puck-font-${font.uuid}`;
  const googleFamily = font.googleFamily || 'Inter';
  const googleWeight = font.googleWeight || '400';
  const googleFamilyEscaped = googleFamily.replace(/'/g, '\\\'');
  const googleClassSuffix = `${googleFamily}-${googleWeight}`.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const googlePreviewClass = `puck-fonts-row-preview-google-${googleClassSuffix}`;
  const styleTag = source === 'uploaded' && font.url
    ? `@font-face { font-family: '${uploadedFontFamily}'; src: url('${font.url}'); font-display: swap; }`
    : null;
  const googleCssUrl = source === 'google' ? getGoogleFontCssUrl(googleFamily, googleWeight) : '';
  const googleFileUrl = source === 'google' ? getGoogleFontFileUrl(font) : '';
  const googleStyleTag = source === 'google' && googleCssUrl
    ? `@import url('${googleCssUrl}'); .${googlePreviewClass} { font-family: '${googleFamilyEscaped}', sans-serif !important; font-weight: ${Number(googleWeight)} !important; }`
    : null;
  const googleDownloadBaseName = (font.name || googleFamily || 'font').trim();
  const googleDownloadName = `${googleDownloadBaseName}.ttf`;
  const isMissingGoogleSelection = source === 'google' && !font.googleFamily;
  const isMissingUploadedSelection = source !== 'google' && !font.uuid && !font.url;
  const showMissingPlaceholder = isMissingGoogleSelection || isMissingUploadedSelection;

  if (showMissingPlaceholder && !isEditing) {
    return null;
  }

  const handleGoogleDownload = async (e) => {
    e.preventDefault();
    if (!googleFileUrl) return;

    try {
      const response = await fetch(googleFileUrl);
      if (!response.ok) throw new Error('fetch failed');
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = googleDownloadName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);

      addFlashMessage({ type: 'success', text: intl.formatMessage({ defaultMessage: 'Your font will be downloaded' }) });
    } catch (err) {
      addFlashMessage({ type: 'danger', text: intl.formatMessage({ defaultMessage: 'Could not download this font right now. Please try again.' }) });
    }
  };

  const hex = font.color || '#222222';
  const normalizedHex = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex) ? hex : '#222222';
  const previewStyleSuffix = `${source}-${font.uuid || font.googleFamily || font.name || 'font'}-${normalizedHex}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-');
  const previewStyleClass = `puck-fonts-row-preview-style-${previewStyleSuffix}`;
  const previewStyleTag = `.${previewStyleClass} { color: ${normalizedHex} !important; ${source === 'uploaded' && font.url ? `font-family: '${uploadedFontFamily}', sans-serif !important;` : ''} }`;

  return (
    <div className="puck-fonts-row">
      {googleStyleTag && (
        <style>{googleStyleTag}</style>
      )}
      {styleTag && (
        <style>{styleTag}</style>
      )}
      <style>{previewStyleTag}</style>
      <div className="puck-fonts-row-meta">
        {font.description && (
          <span className="puck-fonts-row-description">
            {font.description}
          </span>
        )}
        <span
          className={`puck-fonts-row-name ${previewStyleClass} ${source === 'google' ? googlePreviewClass : ''}`}
          style={{ visibility: font.name ? 'visible' : 'hidden' }}
        >
          {font.name || ' '}
        </span>
      </div>
      <p
        className={[
          'puck-fonts-row-preview',
          previewStyleClass,
          source === 'google' ? googlePreviewClass : '',
          showMissingPlaceholder ? 'puck-fonts-row-preview-missing' : '',
        ].filter(Boolean).join(' ')}
      >
        {showMissingPlaceholder
          ? intl.formatMessage({ defaultMessage: 'Font not added yet. Select a font in settings.' })
          : previewText}
      </p>
      <div className="puck-fonts-row-buttons-wrapper">
        {allowDownloads && source === 'uploaded' && font.url && (
        <Button
          size="sm"
          color="link"
          className="puck-fonts-row-download"
          href={font.url}
          download={font.fileName || font.name || 'font'}
        >
          <i className="far fa-download me-2" />
          {intl.formatMessage({ defaultMessage: 'Download typeface' })}
        </Button>
        )}
        {allowDownloads && source === 'google' && googleFileUrl && (
        <Button
          size="sm"
          color="link"
          className="puck-fonts-row-download-btn"
          onClick={handleGoogleDownload}
        >
          <i className="far fa-download me-2" />
          {intl.formatMessage({ defaultMessage: 'Download typeface' })}
        </Button>
        )}
      </div>
    </div>
  );
};

FontRow.propTypes = {
  font: PropTypes.shape({
    source: PropTypes.oneOf(['uploaded', 'google']),
    uuid: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    color: PropTypes.string,
    url: PropTypes.string,
    fileName: PropTypes.string,
    googleFamily: PropTypes.string,
    googleWeight: PropTypes.string,
    googleFiles: PropTypes.object,
  }).isRequired,
  previewText: PropTypes.string.isRequired,
  allowDownloads: PropTypes.bool.isRequired,
  isEditing: PropTypes.bool,
};

FontRow.defaultProps = {
  isEditing: false,
};

export default FontRow;
